/**
 * Google reCAPTCHA v3 helper.
 *
 * The script may already be loaded by CookieConsent (when the user accepted
 * "statistics" cookies). For form submissions we always need a token, so this
 * helper loads the script on demand if it isn't there yet — spam protection
 * is essential for the form to function and is therefore loaded regardless of
 * the marketing/analytics consent state.
 */

export const RECAPTCHA_SITE_KEY = '6LeSokYrAAAAALyGmEFwUzB29OBC-xTv1Ueayyac';

const SCRIPT_SRC = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;

let loadingPromise: Promise<void> | null = null;

const loadScript = (): Promise<void> => {
  if (window.grecaptcha) return Promise.resolve();
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src^="https://www.google.com/recaptcha/api.js"]`);
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      loadingPromise = null;
      reject(new Error('Failed to load reCAPTCHA'));
    });
    if (window.grecaptcha) resolve();
  });

  return loadingPromise;
};

export const executeRecaptcha = async (action: string): Promise<string> => {
  await loadScript();
  if (!window.grecaptcha) throw new Error('reCAPTCHA not available');

  return new Promise<string>((resolve, reject) => {
    window.grecaptcha!.ready(() => {
      window.grecaptcha!
        .execute(RECAPTCHA_SITE_KEY, { action })
        .then(resolve)
        .catch(reject);
    });
  });
};
